/** Classify axios/API errors for consistent frontend handling. */

export const ApiErrorKind = {
  NETWORK: 'network',
  TIMEOUT: 'timeout',
  UNAUTHORIZED: '401',
  FORBIDDEN: '403',
  NOT_FOUND: '404',
  CONFLICT: '409',
  VALIDATION: '422',
  RATE_LIMIT: '429',
  SERVER: '5xx',
  UNKNOWN: 'unknown',
};

export function isBrowserOnline() {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

export function classifyApiError(error) {
  if (!error) {
    return { kind: ApiErrorKind.UNKNOWN, message: 'Something went wrong', raw: error };
  }

  if (error.code === 'ECONNABORTED' || String(error.message || '').toLowerCase().includes('timeout')) {
    return {
      kind: ApiErrorKind.TIMEOUT,
      message: 'Request timed out. Check your connection and try again.',
      raw: error,
    };
  }

  if (!error.response) {
    if (!isBrowserOnline()) {
      return {
        kind: ApiErrorKind.NETWORK,
        message: 'No internet connection',
        raw: error,
      };
    }
    const msg = String(error.message || '');
    if (msg === 'Network Error' || error.code === 'ERR_NETWORK') {
      return {
        kind: ApiErrorKind.NETWORK,
        message: 'Cannot reach RESQLY servers. Check your internet connection.',
        raw: error,
      };
    }
    return {
      kind: ApiErrorKind.NETWORK,
      message: 'Network request failed',
      raw: error,
    };
  }

  const status = error.response.status;
  const detail = error.response.data?.detail;
  const message = typeof detail === 'string' ? detail : error.message || 'Request failed';

  if (status === 401) return { kind: ApiErrorKind.UNAUTHORIZED, message, status, raw: error };
  if (status === 403) return { kind: ApiErrorKind.FORBIDDEN, message, status, raw: error };
  if (status === 404) return { kind: ApiErrorKind.NOT_FOUND, message, status, raw: error };
  if (status === 409) return { kind: ApiErrorKind.CONFLICT, message, status, raw: error };
  if (status === 422) return { kind: ApiErrorKind.VALIDATION, message, status, raw: error };
  if (status === 429) return { kind: ApiErrorKind.RATE_LIMIT, message, status, raw: error };
  if (status >= 500) {
    return {
      kind: ApiErrorKind.SERVER,
      message: detail || 'RESQLY is temporarily unavailable. Please try again.',
      status,
      raw: error,
    };
  }

  return { kind: ApiErrorKind.UNKNOWN, message, status, raw: error };
}

export function isNetworkFailure(error) {
  const kind = error?.resqly?.kind || classifyApiError(error).kind;
  return kind === ApiErrorKind.NETWORK || kind === ApiErrorKind.TIMEOUT;
}

export function isAuthFailure(error) {
  const kind = error?.resqly?.kind || classifyApiError(error).kind;
  return kind === ApiErrorKind.UNAUTHORIZED;
}

export const MUTATION_OFFLINE_MESSAGE =
  'Connection lost. We could not confirm this action. Please check your connection and try again.';

export function getApiErrorMessage(error, fallback = 'Something went wrong') {
  if (error?.resqly?.message) return error.resqly.message;
  return classifyApiError(error).message || fallback;
}
